// Selector reutilizable de proyecto + formulario o acumulado. Devuelve los ids elegidos.
import { useQuery } from "@tanstack/react-query";
import { OdkAPI, DatasetsAPI } from "../api/endpoints";
import { AccionesSeleccion } from "./Layout";

export interface ProjectFormPickerProps {
  projectId: number | null;
  formId: string | null;
  onProject: (id: number) => void;
  onForm: (id: string) => void;
  multipleForms?: boolean;
  selectedForms?: string[];
  onToggleForm?: (id: string) => void;
  /** Con esto aparecen «Todas / Ninguna» sobre la lista de encuestas. */
  onSetForms?: (ids: string[]) => void;
}

export default function ProjectFormPicker(props: ProjectFormPickerProps) {
  const {
    projectId, formId, onProject, onForm,
    multipleForms, selectedForms, onToggleForm, onSetForms,
  } = props;
  const projectsQ = useQuery({ queryKey: ["projects"], queryFn: OdkAPI.projects });
  const formsQ = useQuery({
    queryKey: ["forms", projectId],
    queryFn: () => OdkAPI.forms(projectId!),
    enabled: !!projectId,
  });
  const datasetsQ = useQuery({
    queryKey: ["datasets"],
    queryFn: DatasetsAPI.list,
  });

  const projectDatasets = (datasetsQ.data ?? []).filter(
    (d) => d.project_id === projectId && !!d.form_id
  );

  const allVisibleFormIds = [
    ...(formsQ.data ?? []).map((f) => f.xmlFormId),
    ...projectDatasets.map((d) => d.form_id!),
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="etiqueta">Proyecto</label>
        <select
          className="input"
          value={projectId ?? ""}
          onChange={(e) => onProject(Number(e.target.value))}
        >
          <option value="">— elegir —</option>
          {projectsQ.data?.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      <div>
        {multipleForms ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <label className="etiqueta mb-0">
                Encuestas y acumulados — marca todas las fuentes que quieras comparar
              </label>
              {onSetForms && (
                <AccionesSeleccion
                  visibles={allVisibleFormIds}
                  seleccionados={selectedForms ?? []}
                  onCambiar={onSetForms}
                  nombre="fuente(s)"
                />
              )}
            </div>
            <div className="max-h-48 overflow-y-auto bg-white border border-grid rounded-md p-2">
              {!projectId && <div className="text-xs text-tinta-3 p-1">Elige primero un proyecto.</div>}
              {formsQ.data?.map((f) => (
                <label key={f.xmlFormId} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-marca"
                    checked={selectedForms?.includes(f.xmlFormId) ?? false}
                    onChange={() => onToggleForm?.(f.xmlFormId)}
                  />
                  <span>{f.name}</span>
                </label>
              ))}
              {projectDatasets.length > 0 && (
                <div className="pt-2 mt-2 border-t border-grid">
                  <div className="text-xs font-semibold text-tinta-2 mb-1">Acumulados / Fusiones:</div>
                  {projectDatasets.map((d) => (
                    <label key={d.form_id!} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer text-marca">
                      <input
                        type="checkbox"
                        className="accent-marca"
                        checked={selectedForms?.includes(d.form_id!) ?? false}
                        onChange={() => onToggleForm?.(d.form_id!)}
                      />
                      <span className="font-medium">📊 {d.name} ({d.n} casos)</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {!onSetForms && selectedForms && selectedForms.length > 0 && (
              <div className="mt-1 text-xs text-marca">{selectedForms.length} fuente(s) seleccionada(s)</div>
            )}
          </>
        ) : (
          <>
            <label className="etiqueta">Formulario o Acumulado</label>
            <select
              className="input disabled:opacity-50"
              disabled={!projectId}
              value={formId ?? ""}
              onChange={(e) => onForm(e.target.value)}
            >
              <option value="">— elegir —</option>
              {formsQ.data && formsQ.data.length > 0 && (
                <optgroup label="Encuestas individuales (ODK)">
                  {formsQ.data.map((f) => (
                    <option key={f.xmlFormId} value={f.xmlFormId}>{f.name}</option>
                  ))}
                </optgroup>
              )}
              {projectDatasets.length > 0 && (
                <optgroup label="Acumulados y Fusiones (Trackings)">
                  {projectDatasets.map((d) => (
                    <option key={d.form_id!} value={d.form_id!}>
                      📊 {d.name} ({d.n} casos · Rev {d.current_revision_id})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </>
        )}
      </div>
    </div>
  );
}
